import { useState } from 'react';
import type { ExpenseCategory, ExpenseFormData, ExpenseStatus, LineItem } from '@/lib/types';
import { CATEGORY_LABELS } from '@/lib/types';
import { Icons } from '@/components/icons';
import { fmtUSD } from '@/lib/format';
import { formatInstantForLocalDatetimeInput, localDatetimeInputToUtcIso, nowAsLocalDatetimeInput } from '@/lib/utils';

interface Props {
  mode: 'create' | 'edit';
  initialData?: ExpenseFormData;
  autoExtracted?: boolean;
  onSubmit: (data: ExpenseFormData) => Promise<void>;
  onCancel?: () => void;
}

type DisplayItem = LineItem & { _unitPriceStr: string };

const defaultItem = (): DisplayItem => ({ name: '', quantity: 1, unit_price: 0, total: 0, category: 'other', _unitPriceStr: '' });
const CATEGORY_KEYS = Object.keys(CATEGORY_LABELS) as ExpenseCategory[];

const DECIMAL_RE = /^\d*\.?\d*$/;

export function ExpenseForm({ mode, initialData, autoExtracted, onSubmit, onCancel }: Props) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [storeName, setStoreName] = useState(() => initialData?.store_name || '');
  const [transactionDate, setTransactionDate] = useState(() =>
    initialData?.transaction_date ? formatInstantForLocalDatetimeInput(initialData.transaction_date) : nowAsLocalDatetimeInput()
  );
  const [items, setItems] = useState<DisplayItem[]>(() =>
    (initialData?.items?.length ? initialData.items : [defaultItem()]).map(item => ({
      ...item,
      _unitPriceStr: item.unit_price ? item.unit_price.toFixed(2) : '',
    }))
  );
  const [tax, setTax] = useState(() => initialData?.tax || 0);
  const [taxStr, setTaxStr] = useState(() => { const v = initialData?.tax || 0; return v ? v.toFixed(2) : ''; });
  const [discount, setDiscount] = useState(() => initialData?.discount ?? 0);
  const [discountStr, setDiscountStr] = useState(() => { const v = initialData?.discount ?? 0; return v ? v.toFixed(2) : ''; });
  const [status, setStatus] = useState<ExpenseStatus>(() => initialData?.status || 'pending');
  const [notes, setNotes] = useState(() => initialData?.notes || '');

  const subtotal = Number(items.reduce((sum, item) => sum + item.total, 0).toFixed(2));
  const total = Number((subtotal - discount + tax).toFixed(2));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validItems = items
      .filter((item) => item.name.trim())
      .map(({ name, quantity, unit_price, total, category }) => ({
        name,
        quantity,
        unit_price,
        total,
        category,
      }));
    if (validItems.length === 0) {
      setError('Please add at least one item.');
      return;
    }

    const transactionDateUtc = localDatetimeInputToUtcIso(transactionDate);
    if (!transactionDateUtc) {
      setError('Please enter a valid date and time.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await onSubmit({
        id: initialData?.id,
        transaction_date: transactionDateUtc,
        store_name: storeName,
        items: validItems,
        subtotal,
        tax,
        discount,
        total: total || subtotal - discount + tax,
        status,
        notes,
      });
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Failed to save');
    }

    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} noValidate style={{ maxWidth: 920 }}>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-h">
          <h3>Receipt details</h3>
          {autoExtracted && (
            <span className="badge" style={{ background: 'var(--primary-soft)', color: 'oklch(0.4 0.12 55)' }}>
              <Icons.Sparkle size={11} /> Auto-extracted · review &amp; save
            </span>
          )}
        </div>
        <div style={{ padding: 18 }}>
          <div className="form-grid">
            <div className="field">
              <label>Store</label>
              <input value={storeName} onChange={(e) => setStoreName(e.target.value)} placeholder="e.g. Food Lion" />
            </div>
            <div className="field">
              <label>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as ExpenseStatus)}>
                <option value="pending">Pending</option>
                <option value="reimbursed">Reimbursed</option>
                <option value="donation">Donated</option>
              </select>
            </div>
            <div className="field full">
              <label>Transaction date &amp; time</label>
              <input type="datetime-local" value={transactionDate} onChange={(e) => setTransactionDate(e.target.value)} />
            </div>
            <div className="field full">
              <label>Notes (optional)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything reviewers should know — donation source, splits, etc." />
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-h">
          <h3>Line items</h3>
          <span className="sub">{items.length} {items.length === 1 ? 'item' : 'items'}</span>
          <div className="spacer" />
          <button type="button" className="btn sm" onClick={() => setItems([...items, defaultItem()])}>
            <Icons.Plus size={13} /> Add item
          </button>
        </div>
        <div style={{ padding: '10px 18px 18px' }}>
          <div className="item-row head">
            <div>Item</div><div>Category</div><div>Qty</div><div>Unit price</div><div>Total</div><div />
          </div>
          {items.map((it, i) => (
            <div key={i} className="item-row">
              <input className="small" value={it.name} onChange={(e) => {
                const updated = [...items];
                updated[i] = { ...updated[i], name: e.target.value };
                setItems(updated);
              }} placeholder="e.g. FL WHOLE MILK GALLON" />
              <select className="small" value={it.category} onChange={(e) => {
                const updated = [...items];
                updated[i] = { ...updated[i], category: e.target.value as ExpenseCategory };
                setItems(updated);
              }}>
                {CATEGORY_KEYS.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
              </select>
              <input className="small num-in" type="number" min="0" step="1" value={it.quantity || ''} onChange={(e) => {
                const updated = [...items];
                const item = { ...updated[i] };
                item.quantity = Math.max(0, Number(e.target.value) || 0);
                item.total = Number((item.quantity * item.unit_price).toFixed(2));
                updated[i] = item;
                setItems(updated);
              }} />
              <input
                className="small num-in"
                type="text"
                inputMode="decimal"
                value={it._unitPriceStr}
                onChange={(e) => {
                  const str = e.target.value;
                  if (str !== '' && !DECIMAL_RE.test(str)) return;
                  const updated = [...items];
                  const item = { ...updated[i] };
                  item._unitPriceStr = str;
                  item.unit_price = Math.max(0, Number(str) || 0);
                  item.total = Number((item.quantity * item.unit_price).toFixed(2));
                  updated[i] = item;
                  setItems(updated);
                }}
                onBlur={() => {
                  const updated = [...items];
                  const item = { ...updated[i] };
                  item._unitPriceStr = item.unit_price ? item.unit_price.toFixed(2) : '';
                  updated[i] = item;
                  setItems(updated);
                }}
              />
              <div className="tot">{fmtUSD(it.total)}</div>
              <button
                type="button"
                className="rm"
                onClick={() => setItems(items.length <= 1 ? items : items.filter((_, idx) => idx !== i))}
                title="Remove item"
                disabled={items.length <= 1}
              >
                <Icons.Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="card-h">
          <h3>Totals</h3>
          <span className="sub">Auto-calculated from items; edit tax &amp; discount to balance</span>
        </div>
        <div style={{ padding: 18, display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: 'min(320px, 100%)' }}>
            <div className="totals" style={{ borderTop: 0, paddingTop: 0, marginTop: 0 }}>
              <div className="row"><span>Subtotal</span><span className="num">{fmtUSD(subtotal)}</span></div>
              <div className="row" style={{ alignItems: 'center' }}>
                <span>Discount</span>
                <input
                  aria-label="Discount"
                  type="text"
                  inputMode="decimal"
                  value={discountStr}
                  onChange={(e) => {
                    const str = e.target.value;
                    if (str !== '' && !DECIMAL_RE.test(str)) return;
                    setDiscountStr(str);
                    setDiscount(Math.max(0, Number(str) || 0));
                  }}
                  onBlur={(e) => {
                    const num = Math.max(0, Number(e.target.value) || 0);
                    setDiscount(num);
                    setDiscountStr(num ? num.toFixed(2) : '');
                  }}
                  style={{ width: 90, textAlign: 'right', padding: '4px 8px', border: '1px solid var(--line)', borderRadius: 6, background: 'var(--surface)', fontVariantNumeric: 'tabular-nums', outline: 0 }}
                />
              </div>
              <div className="row" style={{ alignItems: 'center' }}>
                <span>Tax</span>
                <input
                  aria-label="Tax"
                  type="text"
                  inputMode="decimal"
                  value={taxStr}
                  onChange={(e) => {
                    const str = e.target.value;
                    if (str !== '' && !DECIMAL_RE.test(str)) return;
                    setTaxStr(str);
                    setTax(Math.max(0, Number(str) || 0));
                  }}
                  onBlur={(e) => {
                    const num = Math.max(0, Number(e.target.value) || 0);
                    setTax(num);
                    setTaxStr(num ? num.toFixed(2) : '');
                  }}
                  style={{ width: 90, textAlign: 'right', padding: '4px 8px', border: '1px solid var(--line)', borderRadius: 6, background: 'var(--surface)', fontVariantNumeric: 'tabular-nums', outline: 0 }}
                />
              </div>
              <div className="row grand"><span>Total</span><span className="num">{fmtUSD(total)}</span></div>
            </div>

            {error && (
              <p style={{ marginTop: 12, padding: '8px 11px', borderRadius: 8, background: 'oklch(0.96 0.03 25)', color: 'oklch(0.45 0.16 25)', fontSize: 12.5 }}>
                {error}
              </p>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
              {onCancel && <button type="button" className="btn ghost" onClick={onCancel}>Cancel</button>}
              <button type="submit" className="btn primary" disabled={saving}>
                <Icons.Check size={14} /> {saving ? 'Saving…' : mode === 'create' ? 'Save receipt' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
